import type { DataScope } from "../workflowData/models";
import { getHeaders } from "../../api/api";
import type { NoteWorkflow, NoteWorkflowsDocument } from "../../components/NoteWorkflows/noteWorkflowsTypes";
import { readWorkflowBuilderResponse } from "../workflowBuilder/response";
import { boundedChatHistory, organizationChatResponseSchema, organizationPreviewSelectionSchema, organizationSaveResponseSchema } from "./models";
import type { OrganizationChatRequest, OrganizationChatResponse, OrganizationPreviewBox } from "./models";
import { serializeNativeWorkflow } from "./nativeWorkflow";

type WorkflowPayload = ReturnType<typeof serializeNativeWorkflow>;
type ChatPayload = Omit<OrganizationChatRequest, "workflow"> & { workflow: WorkflowPayload | null };
type SavePayload = { workflow: WorkflowPayload; base_workflow?: WorkflowPayload };
type PreviewSelectionPayload = {
    workflow: WorkflowPayload;
    sample: string;
    language: string;
    node_id: string;
    value: string;
    project_id: string | null;
    data_scope?: DataScope | null;
};

const base = String(import.meta.env.VITE_BACKEND_URI ?? "").replace(/\/$/, "");
async function request(
    organizationId: string,
    action: "chat" | "save" | "preview-selection",
    body: ChatPayload | SavePayload | PreviewSelectionPayload,
    token: string | undefined,
    signal: AbortSignal
): Promise<unknown> {
    const response = await fetch(`${base}/organizations/${encodeURIComponent(organizationId)}/workflow-builder/${action}`, {
        method: "POST",
        cache: "no-store",
        signal,
        headers: { ...(await getHeaders(token)), "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });
    return readWorkflowBuilderResponse(response);
}

export async function proposeOrganizationWorkflow(
    organizationId: string,
    body: OrganizationChatRequest,
    token: string | undefined,
    signal: AbortSignal
): Promise<OrganizationChatResponse> {
    return organizationChatResponseSchema.parse(
        await request(
            organizationId,
            "chat",
            {
                ...body,
                handover: body.handover ?? null,
                workflow: body.workflow ? serializeNativeWorkflow(body.workflow) : null,
                messages: boundedChatHistory(body.messages)
            },
            token,
            signal
        )
    );
}

export async function saveOrganizationWorkflow(
    organizationId: string,
    workflow: NoteWorkflow,
    token: string | undefined,
    signal: AbortSignal,
    baseWorkflow: NoteWorkflow | null = null
): Promise<NoteWorkflowsDocument> {
    const body: SavePayload = {
        workflow: serializeNativeWorkflow(workflow),
        ...(baseWorkflow ? { base_workflow: serializeNativeWorkflow(baseWorkflow) } : {})
    };
    return organizationSaveResponseSchema.parse(await request(organizationId, "save", body, token, signal)).note_workflows;
}

export async function deleteOrganizationWorkflow(
    organizationId: string,
    workflow: NoteWorkflow,
    token: string | undefined,
    signal: AbortSignal
): Promise<NoteWorkflowsDocument> {
    const response = await fetch(`${base}/organizations/${encodeURIComponent(organizationId)}/workflow-builder/workflows/${encodeURIComponent(workflow.id)}`, {
        method: "DELETE",
        cache: "no-store",
        signal,
        headers: { ...(await getHeaders(token)), "Content-Type": "application/json" },
        body: JSON.stringify({ base_workflow: serializeNativeWorkflow(workflow) })
    });
    return organizationSaveResponseSchema.parse(await readWorkflowBuilderResponse(response)).note_workflows;
}

export async function previewOrganizationSelection(
    organizationId: string,
    workflow: NoteWorkflow,
    sample: string,
    nodeId: string,
    value: string,
    language: string,
    token: string | undefined,
    signal: AbortSignal,
    projectId: string | null = null,
    dataScope: DataScope | null = null
): Promise<OrganizationPreviewBox> {
    const result = organizationPreviewSelectionSchema.parse(
        await request(
            organizationId,
            "preview-selection",
            {
                workflow: serializeNativeWorkflow(workflow),
                sample,
                node_id: nodeId,
                value,
                language,
                project_id: projectId,
                data_scope: dataScope
            },
            token,
            signal
        )
    );
    if (result.box.box_id !== nodeId || result.box.selected_value !== value) throw new Error("The preview returned a different selection. Try again.");
    return result.box;
}
